import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Ui } from '@silvertours/front-shared';
import { RegionalSettingsTrigger } from '../../RegionalSettings';

const ANIMATION_DURATION_MS = 300;

type Floating = { $isFloating: boolean };

const withoutTransientProps = {
  shouldForwardProp: (prop: string) => !prop.startsWith('$'),
};

/**
 * Wraps the regular menu contents (trigger + links). Marked inert while the
 * submenu is open so the covered controls leave the tab order.
 *
 * In the floating header, the trigger is beside the links below md and above
 * them from md (dropdown card). The open submenu needs more width than the
 * dropdown card.
 */
const BackgroundContent = styled.div<Floating & { $isSubmenuVisible: boolean }>`
  width: 100%;

  ${({ $isFloating, $isSubmenuVisible, theme }) =>
    $isFloating &&
    css`
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: ${theme.spacing[40]};

      ${theme.mq('md')} {
        flex-direction: column;
        align-items: flex-end;
        gap: ${theme.spacing[20]};
        min-width: ${$isSubmenuVisible ? '20rem' : 'auto'};
      }
    `}
`;

const StyledRegionalSettingsTrigger = styled(
  RegionalSettingsTrigger,
  withoutTransientProps,
)<Floating>`
  margin-bottom: ${({ theme }) => theme.spacing[40]};

  ${({ $isFloating, theme }) =>
    $isFloating &&
    css`
      width: auto;
      margin-bottom: 0;
      padding: 0;
      font-family: ${theme.fontFamily.alternate};
      font-size: ${theme.fontSize.sm.size};
      font-weight: ${theme.fontWeight.heading};
      line-height: ${theme.fontSize.sm.lineHeight};

      /* The flag sets a smaller label size. */
      > span {
        font-size: inherit;
      }
    `}
`;

/**
 * Clipping layer covering the whole panel (including the shell's close button).
 * It is transparent to pointer events so the menu stays interactive while the
 * submenu is hidden off-canvas; the sliding Submenu re-enables them.
 */
const SubmenuLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
`;

const Submenu = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;

  /* Align the form with the regular menu content (and the account dropdown),
     which start at StyledContent's top padding rather than the panel top. */
  padding-top: ${({ theme }) => theme.spacing[50]};
  overflow-y: auto;
  pointer-events: auto;
  background-color: ${({ theme }) => theme.colors.neutral.white100};
  will-change: transform;

  ${({ $isVisible }) => css`
    transform: translateX(${$isVisible ? '0' : '100%'});
    visibility: ${$isVisible ? 'visible' : 'hidden'};
    transition:
      transform ${ANIMATION_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1),
      visibility ${ANIMATION_DURATION_MS}ms;
  `}
`;

/** Same position/appearance as the shell's close button, but returns to the menu. */
const SubmenuCloseButton = styled(Ui.IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing[40]};
  right: ${({ theme }) => theme.spacing[40]};
  margin-right: ${({ theme }) => theme.spacing[20]};
`;

export {
  BackgroundContent,
  StyledRegionalSettingsTrigger,
  Submenu,
  SubmenuCloseButton,
  SubmenuLayer,
};
